import { AxiosMethods, ResourceApi } from '@setvi/shared/enums';
import { axiosHelper, companyResourcesRequest } from '@setvi/shared/services';
import {
  QueryFunctionContext,
  UseInfiniteQueryOptions
} from '@tanstack/react-query';
import { ROUTES } from 'enumsV2';
import { Product, Resource } from '@setvi/shared/interfaces';
import {
  GetCompanyResourcesQuery,
  GetFavoriteResourcesQuery,
  GetFeaturedResourcesQuery,
  GetPagedResourcesQuery,
  GetRecentlyUploadedResourcesQuery,
  GetUserResourcesQuery
} from './Types';

export enum ResourcesQueryKey {
  RESOURCE = 'resource',
  USER_RESOURCES = 'user_resources',
  COMPANY_RESOURCES = 'company_resources',
  FEATURED_RESOURCES = 'featured_resources',
  GET_SHAREABLE_LINK = 'get_shareable_link',
  FAVORITES_RESOURCES = 'favorites_resources',
  RECENTLY_UPLOADED_RESOURCES = 'recently_uploaded_resources',
  PRODUCT_ASSOCIATED_RESOURCES = 'product_associated_resources',
  RESOURCE_V2 = 'resource_v2'
}

interface getCompanyResourcesQueryParams {
  pageSize: number;
  pageNumber: number;
}

export const getCompanyResourcesQuery = (
  categoryId: number,
  params: getCompanyResourcesQueryParams
): UseInfiniteQueryOptions<GetCompanyResourcesQuery> => ({
  queryKey: [ResourcesQueryKey.COMPANY_RESOURCES, categoryId, params],
  queryFn: (data: QueryFunctionContext) =>
    axiosHelper({
      ...companyResourcesRequest(categoryId),
      params: { ...params, pageNumber: data.pageParam || params.pageNumber }
    }),
  select: (res: any) => res,
  getNextPageParam: (
    lastPage: GetCompanyResourcesQuery,
    allPages: GetCompanyResourcesQuery[]
  ) => {
    const total = allPages?.reduce(
      (acc, cur) => acc + cur?.Data?.Result?.length,
      0
    );
    const currentPageNumber = total / params.pageSize;
    return lastPage?.Data?.Count > total ? currentPageNumber + 1 : undefined;
  },
  refetchOnWindowFocus: false,
  enabled: window.location.pathname.includes(ROUTES.RESOURCES)
});

interface getRecentlyUploadedRecourcesQueryParams {
  pageSize: number;
  pageNumber: number;
  orderBy: string;
  sortBy: string;
}

// The v2 resource APIs fixed the historic `MaterilaLastChangeDate` typo; the
// FE (shared home columns, ResourceNewBadge, the remaining v1 surfaces) still
// uses the old spelling. Translate the sort column on the way out…
export const toV2SortBy = (sortBy: string): string =>
  sortBy === 'MaterilaLastChangeDate' ? 'MaterialLastChangeDate' : sortBy;

// …and normalize the item fields on the way back in, at the API boundary. The
// v2 paged envelope (`Data.Items`) is shared by the favorites and featured
// APIs; recently-uploaded has its own shape below.
export const getPagedResources = (page?: GetPagedResourcesQuery): Resource[] =>
  (page?.Data?.Items ?? page?.Data?.Resources ?? []).map(resource => ({
    ...resource,
    MaterilaLastChangeDate:
      resource.MaterilaLastChangeDate ?? resource.MaterialLastChangeDate,
    isInternallyDownloadable:
      resource.isInternallyDownloadable ?? resource.IsInternallyDownloadable
  })) as unknown as Resource[];

export const getRecentlyUploadedResources = (
  page?: GetRecentlyUploadedResourcesQuery
): Resource[] =>
  (page?.Data?.Resources ?? []).map(resource => ({
    ...resource,
    MaterilaLastChangeDate:
      resource.MaterilaLastChangeDate ?? resource.MaterialLastChangeDate
  })) as unknown as Resource[];

export const getRecentlyUploadedResourcesQuery = (
  params: getRecentlyUploadedRecourcesQueryParams
): UseInfiniteQueryOptions<GetRecentlyUploadedResourcesQuery> => ({
  queryKey: [ResourcesQueryKey.RECENTLY_UPLOADED_RESOURCES, params],
  queryFn: (data: QueryFunctionContext) =>
    axiosHelper({
      endpoint: ResourceApi.GetRecentlyUploaded,
      method: AxiosMethods.GET,
      baseURL: process.env.ADMIN_API_BASE_URL,
      params: { ...params, pageNumber: data.pageParam || params.pageNumber }
    }),
  getNextPageParam: (
    lastPage: GetRecentlyUploadedResourcesQuery,
    allPages: GetRecentlyUploadedResourcesQuery[]
  ) => {
    const total = allPages?.reduce(
      (acc, cur) => acc + getRecentlyUploadedResources(cur).length,
      0
    );
    const currentPageNumber = total / params.pageSize;

    return getRecentlyUploadedResources(lastPage).length < params.pageSize
      ? undefined
      : currentPageNumber + 1;
  },
  refetchOnWindowFocus: false
});

interface getFeaturedResourcesQueryParams {
  pageSize: number;
  pageNumber: number;
  orderBy?: string;
  sortBy?: string;
}

export const featuredResourcesQuery = (
  params: getFeaturedResourcesQueryParams
): UseInfiniteQueryOptions<GetFeaturedResourcesQuery> => ({
  queryKey: [ResourcesQueryKey.FEATURED_RESOURCES, params],
  queryFn: (data: QueryFunctionContext) =>
    axiosHelper({
      endpoint: ResourceApi.GetFeatured,
      method: AxiosMethods.GET,
      baseURL: process.env.ADMIN_API_BASE_URL,
      params: { ...params, pageNumber: data.pageParam || params.pageNumber }
    }),
  getNextPageParam: (
    lastPage: GetFeaturedResourcesQuery,
    allPages: GetFeaturedResourcesQuery[]
  ) => {
    const total = allPages?.reduce(
      (acc, cur) => acc + getPagedResources(cur).length,
      0
    );
    const currentPageNumber = total / params.pageSize;

    return getPagedResources(lastPage).length < params.pageSize
      ? undefined
      : currentPageNumber + 1;
  },
  refetchOnWindowFocus: false
});

export const favoritesResourcesQuery = (input?: any) => ({
  queryKey: [ResourcesQueryKey.FAVORITES_RESOURCES, input],
  queryFn: (data: any) =>
    axiosHelper({
      endpoint: ResourceApi.GetFavorite,
      method: AxiosMethods.GET,
      params: data?.queryKey[1]
    }),
  select: (res: any) => res?.Data
});

interface getFavoriteResourcesQueryParams {
  pageNumber: number;
  pageSize: number;
  orderBy: string;
  sortBy: string;
}

// Infinite query implementation
export const getFavoriteResourcesQuery = (
  params: getFavoriteResourcesQueryParams
): UseInfiniteQueryOptions<GetFavoriteResourcesQuery> => ({
  queryKey: [ResourcesQueryKey.FAVORITES_RESOURCES, params],
  queryFn: (data: QueryFunctionContext) =>
    axiosHelper({
      endpoint: ResourceApi.GetFavoriteV2,
      method: AxiosMethods.GET,
      baseURL: process.env.ADMIN_API_BASE_URL,
      params: { ...params, pageNumber: data.pageParam || params.pageNumber }
    }),
  getNextPageParam: (
    lastPage: GetFavoriteResourcesQuery,
    allPages: GetFavoriteResourcesQuery[]
  ) => {
    const total = allPages?.reduce(
      (acc, cur) => acc + getPagedResources(cur).length,
      0
    );
    const currentPageNumber = total / params.pageSize;
    return getPagedResources(lastPage).length < params.pageSize
      ? undefined
      : currentPageNumber + 1;
  },
  refetchOnWindowFocus: false
});

export const getShareableLinkQuery = (input?: any) => ({
  queryKey: [ResourcesQueryKey.GET_SHAREABLE_LINK, input],
  queryFn: (data: any) =>
    axiosHelper({
      endpoint: ResourceApi.GetShareableLink,
      method: AxiosMethods.POST,
      params: data?.queryKey[1]
    }),
  select: (res: any) => res?.Data[0] || null
});

interface getUserResourcesQueryParams {
  pageNumber: number;
  pageSize: number;
}

export const getUserResourcesQuery = (
  categoryId: number,
  params: getUserResourcesQueryParams
): UseInfiniteQueryOptions<GetUserResourcesQuery> => ({
  queryKey: [ResourcesQueryKey.USER_RESOURCES, params, categoryId],
  queryFn: (data: QueryFunctionContext) =>
    axiosHelper({
      endpoint: ResourceApi.GetUserResources.replace(
        ':categoryId',
        categoryId?.toString()
      ),
      method: AxiosMethods.GET,
      params: { ...params, pageNumber: data.pageParam || params.pageNumber }
    }),
  getNextPageParam: (
    lastPage: GetUserResourcesQuery,
    allpages: GetUserResourcesQuery[]
  ) => {
    const total = allpages?.reduce(
      (acc, curr) => acc + curr?.Data?.Result?.length,
      0
    );
    const currentPageNumber = total / params.pageSize;
    return lastPage?.Data?.Result?.length < params.pageSize
      ? undefined
      : currentPageNumber + 1;
  },
  refetchOnWindowFocus: false
});

export const getResource = (Id: string | number) => ({
  queryKey: [ResourcesQueryKey.RESOURCE, Id],
  queryFn: () =>
    axiosHelper({
      endpoint: ResourceApi.GetResource.replace(':resourceId', Id.toString()),
      method: AxiosMethods.GET
    }),
  cacheTime: 0,
  select: (res: any) => res,
  refetchOnWindowFocus: false
});

/**
 * Single resource from the v2 endpoint. Unlike the v1 one it also carries the
 * print-order fields the cart flow needs, so a caller that only has an id can
 * still tell whether the resource takes a QR sheet, a cover or a location.
 */
export const getResourceV2Query = (id: string | number) => ({
  queryKey: [ResourcesQueryKey.RESOURCE_V2, id],
  // Unlike the v1 endpoint, which returns the resource directly, this one wraps
  // it in the standard { Data, Error, Status } envelope. Unwrapped here rather
  // than in `select`, which only runs for useQuery — `queryClient.fetchQuery`
  // would otherwise hand callers the envelope instead of the resource.
  queryFn: async () => {
    const response = await axiosHelper({
      endpoint: ResourceApi.GetResourceV2.replace(':id', id.toString()),
      method: AxiosMethods.GET,
      baseURL: process.env.ADMIN_API_BASE_URL
    });
    return response?.Data ?? null;
  },
  refetchOnWindowFocus: false
});

export const productAssociatedResources = (ProductId: Product['sku']) => ({
  queryKey: [ResourcesQueryKey.PRODUCT_ASSOCIATED_RESOURCES, ProductId],
  queryFn: () =>
    axiosHelper({
      endpoint: ResourceApi.ProductAssociatedResources,
      method: AxiosMethods.POST,
      data: {
        ProductId
      }
    }),
  select: (res: any) => res?.Data
});
